/**
 * DCF Inputs Component
 * Financial inputs for Discounted Cash Flow method
 * Includes Gordon Growth Model toggle option
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const DCFInputs = ({
  formData,
  onInputChange,
  onShowHelp,
  setFormData,
  formatNumberWithCommas
}) => {
  return (
    <div className="col-span-full space-y-4">
      {/* Row 1: Free Cash Flow, Discount Rate, Projection Period, Perpetuity Checkbox */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Free Cash Flow */}
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Free Cash Flow *
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('cash_flow');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-3 text-gray-500">$</span>
            <input
              type="text"
              name="cash_flow"
              value={formatNumberWithCommas(formData.cash_flow)}
              onChange={onInputChange}
              required
              placeholder="800,000"
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Discount Rate */}
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Discount Rate (%)
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('dcf_discount_rate');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <input
              type="number"
              name="dcf_discount_rate"
              value={formData.dcf_discount_rate}
              onChange={onInputChange}
              onFocus={(e) => e.target.select()}
              step="0.1"
              min="0"
              max="100"
              placeholder="15"
              className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="absolute right-3 top-3 text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">Default: 15%</p>
        </div>

        {/* Projection Period - disabled when perpetuity is checked */}
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Projection Period (Years)
          </label>
          <input
            type="number"
            name="dcf_years"
            value={formData.dcf_years || 5}
            onChange={onInputChange}
            onFocus={(e) => e.target.select()}
            step="1"
            min="1"
            max="20"
            placeholder="5"
            disabled={formData.dcf_perpetuity}
            className={`w-full pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.dcf_perpetuity ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          <p className="text-xs text-gray-600 mt-1">{formData.dcf_perpetuity ? 'N/A (Gordon Growth)' : 'Default: 5 years'}</p>
        </div>

        {/* Perpetuity Checkbox */}
        <div className="md:col-span-3 flex items-center">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              name="dcf_perpetuity"
              checked={formData.dcf_perpetuity || false}
              onChange={(e) => setFormData(prev => ({ ...prev, dcf_perpetuity: e.target.checked }))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-xs font-medium text-gray-700">Use Gordon Growth Model</span>
          </label>
        </div>
      </div>

      {/* Row 2: Annual Growth Rate and Terminal Growth Rate side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Annual Growth Rate - disabled when perpetuity is checked */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Annual Growth Rate (%) - Projection Period
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('dcf_annual_growth_rate');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <input
              type="number"
              name="dcf_annual_growth_rate"
              value={formData.dcf_annual_growth_rate || 5}
              onChange={onInputChange}
              onFocus={(e) => e.target.select()}
              step="0.1"
              min="0"
              max="100"
              placeholder="5"
              disabled={formData.dcf_perpetuity}
              className={`w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formData.dcf_perpetuity ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            <span className="absolute right-3 top-3 text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">{formData.dcf_perpetuity ? 'N/A (Gordon Growth)' : 'Default: 5%'}</p>
        </div>

        {/* Terminal Growth Rate - always visible */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Terminal Growth Rate (%)
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('dcf_growth_rate');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          <div className="relative">
            <input
              type="number"
              name="dcf_growth_rate"
              value={formData.dcf_growth_rate || 3}
              onChange={onInputChange}
              onFocus={(e) => e.target.select()}
              step="0.1"
              min="0"
              max="10"
              placeholder="3"
              className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="absolute right-3 top-3 text-gray-500">%</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">Default: 3%</p>
        </div>
      </div>
    </div>
  );
};

DCFInputs.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onShowHelp: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired
};

export default DCFInputs;
